import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
  FC,
} from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'types';
import { getDomainConfig } from 'utils/whiteLabel';
import { SignUpType } from '@heltarchat/shared';

// Define the context type
interface FacebookContextType {
  fbLoaded: boolean;
  setFbLoaded: (loaded: boolean) => void;
}

// Facebook SDK Types
export interface FBAuthResponse {
  accessToken?: string;
  code?: string;
  userID?: string;
  expiresIn?: number;
}

export interface FBLoginResponse {
  authResponse: FBAuthResponse | null;
  status: 'connected' | 'not_authorized' | 'unknown';
}

export interface FBLoginOptions {
  config_id: string;
  response_type: string;
  override_default_response_type: boolean;
  extras: {
    setup: {
      solutionID?: string;
    };
  };
}

export interface FacebookSDK {
  init: (config: {
    appId: string;
    autoLogAppEvents: boolean;
    xfbml: boolean;
    version: string;
  }) => void;
  login: (
    callback: (response: FBLoginResponse) => void,
    options: FBLoginOptions,
  ) => void;
}

declare global {
  interface Window {
    fbAsyncInit: () => void;
    FB?: FacebookSDK;
  }
}

// Create a context with default values
const FacebookContext = createContext<FacebookContextType>({
  fbLoaded: false,
  setFbLoaded: () => {},
});

const loadFacebookSDK = (
  selectedSignUpType: SignUpType | undefined,
  setFbLoaded: (loaded: boolean) => void,
) => {
  // Remove old script and FB object
  const oldScript = document.getElementById('facebook-jssdk');
  if (oldScript) {
    oldScript.remove();
  }
  if (window.FB) {
    delete window.FB;
  }

  setFbLoaded(false);

  // This function will be called by the FB SDK script once it's loaded.
  window.fbAsyncInit = function () {
    const domainName = getDomainConfig().name;
    const defaultAppId =
      selectedSignUpType === SignUpType.KARIX
        ? '1361641208473101'
        : selectedSignUpType === SignUpType.V_CONNECT
          ? '775398091107317'
          : '1079577652910447';
    const appId =
      domainName === 'Superfone' ? '2980097198967857' : defaultAppId;

    if (window.FB) {
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v23.0',
      });
      setFbLoaded(true);
    }
  };

  // Create and append the new script tag.
  const js = document.createElement('script');
  js.id = 'facebook-jssdk';
  js.src = 'https://connect.facebook.net/en_US/sdk.js';
  js.async = true;
  js.defer = true;
  document.head.appendChild(js);
};

export const FacebookProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [fbLoaded, setFbLoaded] = useState(false);
  const isInitialMount = useRef(true);
  const prevSignUpType = useRef<SignUpType | undefined>(undefined);

  const selectedSignUpType = useSelector(
    (state: RootState) => state?.settings?.selectedSignUpType,
  );

  useEffect(() => {
    // On initial mount, load SDK
    if (isInitialMount.current) {
      isInitialMount.current = false;
      loadFacebookSDK(selectedSignUpType, setFbLoaded);
      prevSignUpType.current = selectedSignUpType;
      return;
    }

    // Reload SDK when signUpType changes
    if (selectedSignUpType !== prevSignUpType.current) {
      loadFacebookSDK(selectedSignUpType, setFbLoaded);
      prevSignUpType.current = selectedSignUpType;
    }
  }, [selectedSignUpType]);

  return (
    <FacebookContext.Provider value={{ fbLoaded, setFbLoaded }}>
      {children}
    </FacebookContext.Provider>
  );
};

// Hook to use the Facebook SDK state
export const useFacebook = () => useContext(FacebookContext);
